// Row actions for the Schedules simple view (LeejoeSchedulesView): four small
// chips at the right end of each row.
//
//   Confirm  — the appointment's C / R / U status, settable by clinical admins
//   Orders   — completed vs still missing, across the patient's orders and
//              follow-ups; opens the chart's Orders & followups modal
//   Messages — unread texts from the patient; opens the patient messaging modal
//   Tasks    — every task made from this patient's charts (any visit), whoever
//              is on it; the viewer's own open in Messages → Tasks
//
// ScheduleRowActionsHost loads the data for every row at once (see
// hooks/useScheduleRowActionData.js), mounts the two modals, and hands the
// view a `renderRowActions(row)` to place in each row.
import React, { useCallback, useMemo, useState } from "react";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";
import { FiCalendar, FiCheckSquare, FiClipboard, FiMessageSquare } from "react-icons/fi";
import { canAccessComponent, hasClinicalAdminPermissions, isPatientRole } from "../../config/roles";
import { supabaseSchema } from "../supabaseClient";
import { confirmationStyleFor, sourceMetaFor } from "../table/ConfirmStatusCell";
import { isOverdue } from "../messaging/tasksModel";
import PatientMessagingModal from "../PatientMessagingModal";
import ClinicPatientOrdersModal from "../../pages/medstudent/ClinicPatientOrdersModal";
import useScheduleRowActionData from "../../hooks/useScheduleRowActionData";
import { isImpersonating } from "../../utils/impersonation";
import { openMessages } from "../../utils/messagingBus";
import { normalizeStatus, STATUS_META } from "../../utils/workItemStatus";
import {
  isOnTask,
  isOpenTask,
  monthDay,
  rowSchemaOf,
  shortVisitDate,
  sortTasks,
  splitOrderItems,
  summarizeOrderItems,
} from "../../utils/scheduleRowActions";

const CHIP =
  "inline-flex h-7 items-center justify-center gap-1 rounded-lg px-2 text-[11px] " +
  "font-semibold tabular-nums ring-1 ring-inset transition-colors focus:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50";

// Each chip keeps a minimum width, so the four line up in columns down the
// schedule whatever their counts (a typical count fits inside it).
const WIDTH = {
  confirm: "min-w-[42px]",
  orders: "min-w-[56px]",
  messages: "min-w-[48px]",
  tasks: "min-w-[42px]",
};

// Tailwind's preflight is off in this app, so lists keep the browser's
// bullets unless they are reset here.
const LIST = "m-0 list-none p-0";

// A long list scrolls inside its popover, so the header above it and the
// button below it stay in view. The negative margin puts the scrollbar at the
// popover's edge rather than over the text.
const SCROLL_LIST = "-mx-3 max-h-60 overflow-y-auto px-3";

const TONE = {
  muted: "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50 hover:text-slate-600",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100",
  amber: "bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100",
  orange: "bg-orange-50 text-orange-700 ring-orange-200 hover:bg-orange-100",
  yellow: "bg-yellow-50 text-yellow-800 ring-yellow-300 hover:bg-yellow-100",
  rose: "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100",
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200 hover:bg-indigo-100",
};

// Same colours as the full table's Confirm column (ConfirmStatusCell).
const CONFIRM_TONE = { C: "green", R: "orange", U: "yellow" };
const CONFIRM_SOLID = {
  C: "bg-emerald-600 text-white ring-emerald-600",
  R: "bg-orange-500 text-white ring-orange-500",
  U: "bg-yellow-400 text-yellow-950 ring-yellow-400",
};

const BUTTON =
  "inline-flex w-full items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold " +
  "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";
const PRIMARY = `${BUTTON} bg-slate-900 text-white hover:bg-slate-700`;
const SECONDARY = `${BUTTON} bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50`;

const ICON = "h-3.5 w-3.5 shrink-0";
const SECTION = "mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-400";

const MISSING_SHOWN = 6;
const COMPLETED_SHOWN = 3;

const isLoading = (status) => status === "loading" || status === "idle";

// A chip's popover. Deliberately NOT in a Portal: tailwind.config.js scopes
// every utility under `#clinic-root`, and a Portal mounts on document.body,
// outside it, where the content would render unstyled. `strategy="fixed"`
// instead keeps the schedule card's `overflow-hidden` from clipping it on the
// last rows. Clicks inside bubble to the row's action wrapper, which stops
// them before they open the chart.
//
// A fixed popover does not scroll with the page, so anything past the bottom
// of the window would be unreachable: its height is capped to the window and
// the body scrolls inside it (the long lists also scroll on their own, see
// SCROLL_LIST, so the buttons under them stay in view).
//
// The app's floating bubbles (bottom-right) sit at z-index 9998, above
// Chakra's popover default of 1500, and covered the popover's bottom button
// on the lower rows; an open popover goes just above them.
const POPOVER_Z = 9999;

function ActionPopover({ label, width, trigger, children }) {
  return (
    <Popover placement="bottom-end" strategy="fixed" isLazy gutter={6}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>{trigger}</PopoverTrigger>
          <PopoverContent
            rootProps={{ zIndex: POPOVER_Z }}
            aria-label={label}
            width={width}
            maxW="calc(100vw - 2rem)"
            maxH="calc(100vh - 6rem)"
            bg="white"
            color="gray.800"
            borderColor="gray.200"
            rounded="xl"
            boxShadow="lg"
            cursor="default"
            _focus={{ outline: "none" }}
          >
            <PopoverArrow bg="white" />
            <PopoverBody p={3} minH={0} overflowY="auto">
              {children({ onClose })}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}

function MoreLine({ count }) {
  return <li className="py-1 text-[11px] text-slate-400">+ {count} more</li>;
}

// ── Confirm ─────────────────────────────────────────────────────────────────

function ConfirmAction({ row, onSetStatus, onViewMessages }) {
  const style = confirmationStyleFor(row?.confirmation_status);
  const asked = monthDay(row?.confirmation_sent_at);
  const replied =
    row?.confirmation_status && row?.confirmation_source === "sms"
      ? monthDay(row?.confirmation_updated_at)
      : "";
  const source = sourceMetaFor(row?.confirmation_source, row?.confirmation_updated_by);
  return (
    <ActionPopover
      label="Appointment confirmation"
      width="15rem"
      trigger={
        <button
          type="button"
          className={`${CHIP} ${WIDTH.confirm} ${TONE[CONFIRM_TONE[style.label]]}`}
          aria-label={`Confirmation: ${style.title}`}
          title={`Confirmation: ${style.title}`}
        >
          <FiCalendar aria-hidden="true" className={ICON} />
          {style.label}
        </button>
      }
    >
      {({ onClose }) => (
        <div className="text-xs">
          <div className="text-sm font-semibold text-slate-900">{style.title}</div>
          <div className="mt-0.5 text-slate-500">
            {asked ? `Asked ${asked}` : "Not yet asked"}
            {replied ? ` · Replied ${replied}` : ""}
          </div>
          {source && <div className="mt-0.5 text-slate-500">{source.tooltip}</div>}
          {onSetStatus && (
            <>
              <div className={SECTION}>Set status</div>
              <div className="mt-1 flex gap-1">
                {["C", "R", "U"].map((s) => {
                  const active = style.label === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      aria-label={`Set ${s}`}
                      aria-pressed={active}
                      title={confirmationStyleFor(s).title}
                      onClick={() => onSetStatus(row.id, s)}
                      className={`${CHIP} flex-1 ${active ? CONFIRM_SOLID[s] : TONE[CONFIRM_TONE[s]]}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {onViewMessages && (
            <button
              type="button"
              className={`${SECONDARY} mt-3`}
              onClick={() => {
                onClose();
                onViewMessages(row);
              }}
            >
              View messages
            </button>
          )}
        </div>
      )}
    </ActionPopover>
  );
}

// ── Orders ──────────────────────────────────────────────────────────────────

function OrderLine({ item, done }) {
  const meta = STATUS_META[normalizeStatus(item?.status)] || STATUS_META.unassigned;
  const details = [
    item?.item_type === "followup" ? "Follow-up" : null,
    shortVisitDate(item?.scheduled_date),
    !done ? item?.assigned_to?.name || item?.assigned_to?.email : null,
  ].filter(Boolean);
  return (
    <li className="flex items-start gap-2 py-1">
      <span
        aria-hidden="true"
        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${done ? "bg-emerald-500" : "bg-amber-500"}`}
      />
      <div className="min-w-0 flex-1">
        <div className={`break-words leading-snug ${done ? "text-slate-500" : "text-slate-800"}`}>
          {item?.description || "Untitled item"}
        </div>
        {(!done || details.length > 0) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-500">
            {!done && (
              <span className={`rounded-full px-1.5 py-px font-medium ring-1 ${meta.tw}`}>{meta.label}</span>
            )}
            {details.length > 0 && <span>{details.join(" · ")}</span>}
          </div>
        )}
      </div>
    </li>
  );
}

function OrdersAction({ row, items, status, onOpenOrders }) {
  const { total, completed, missing } = summarizeOrderItems(items);
  const lists = useMemo(() => splitOrderItems(items), [items]);
  const loading = isLoading(status);
  const failed = status === "error";
  const tone = loading || failed || total === 0 ? "muted" : missing > 0 ? "amber" : "green";
  const summary = loading
    ? "loading"
    : failed
      ? "couldn't load"
      : total === 0
        ? "none on file"
        : `${completed} completed, ${missing} still missing`;
  return (
    <ActionPopover
      label="Orders and follow-ups"
      width="20rem"
      trigger={
        <button
          type="button"
          className={`${CHIP} ${WIDTH.orders} ${TONE[tone]}`}
          aria-label={`Orders: ${summary}`}
          title={`Orders: ${summary}`}
        >
          <FiClipboard aria-hidden="true" className={ICON} />
          {!loading && !failed && total > 0 && (
            <span>
              {completed}/{total}
            </span>
          )}
        </button>
      }
    >
      {({ onClose }) => (
        <div className="text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-semibold text-slate-900">Orders &amp; follow-ups</div>
            {total > 0 && (
              <div className="shrink-0 tabular-nums text-slate-500">
                {completed} of {total} done
              </div>
            )}
          </div>
          {total > 0 && (
            <div
              className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-amber-100"
              role="progressbar"
              aria-label="Orders completed"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={completed}
            >
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${Math.round((completed / total) * 100)}%` }}
              />
            </div>
          )}
          {loading && <p className="mt-2 text-slate-500">Loading orders…</p>}
          {failed && <p className="mt-2 text-rose-600">Couldn't load orders. Try again in a minute.</p>}
          {!loading && !failed && total === 0 && (
            <p className="mt-2 text-slate-500">No orders or follow-ups on file for this patient.</p>
          )}
          {total > 0 && (
            <div className={SCROLL_LIST}>
              {lists.missing.length > 0 && (
                <>
                  <div className={SECTION}>Still missing · {lists.missing.length}</div>
                  <ul className={LIST}>
                    {lists.missing.slice(0, MISSING_SHOWN).map((item, i) => (
                      <OrderLine key={item.id || `m-${i}`} item={item} />
                    ))}
                    {lists.missing.length > MISSING_SHOWN && (
                      <MoreLine count={lists.missing.length - MISSING_SHOWN} />
                    )}
                  </ul>
                </>
              )}
              {lists.completed.length > 0 && (
                <>
                  <div className={SECTION}>Completed · {lists.completed.length}</div>
                  <ul className={LIST}>
                    {lists.completed.slice(0, COMPLETED_SHOWN).map((item, i) => (
                      <OrderLine key={item.id || `c-${i}`} item={item} done />
                    ))}
                    {lists.completed.length > COMPLETED_SHOWN && (
                      <MoreLine count={lists.completed.length - COMPLETED_SHOWN} />
                    )}
                  </ul>
                </>
              )}
            </div>
          )}
          {onOpenOrders && (
            <button
              type="button"
              className={`${PRIMARY} mt-3`}
              onClick={() => {
                onClose();
                onOpenOrders(row);
              }}
            >
              Open orders
            </button>
          )}
        </div>
      )}
    </ActionPopover>
  );
}

// ── Messages ────────────────────────────────────────────────────────────────

function MessagesAction({ row, unread, hasPhone, onOpen }) {
  // Same rule as the patient page's MSG button: no messaging as someone else.
  const readOnly = isImpersonating();
  const summary = readOnly
    ? "read-only while viewing as another user"
    : unread > 0
      ? `${unread} unread`
      : hasPhone
        ? "no unread texts"
        : "no phone number on file";
  return (
    <button
      type="button"
      className={`${CHIP} ${WIDTH.messages} ${unread > 0 ? TONE.rose : TONE.muted}`}
      aria-label={`Messages: ${summary}`}
      title={`Messages: ${summary}`}
      disabled={readOnly}
      onClick={() => onOpen(row, unread)}
    >
      <FiMessageSquare aria-hidden="true" className={ICON} />
      {unread > 0 && <span>{unread > 99 ? "99+" : unread}</span>}
    </button>
  );
}

// ── Tasks ───────────────────────────────────────────────────────────────────

// One task. Every task made from the patient's charts is listed, whoever is on
// it; only the viewer's own (assigned to or by them) can open in the Tasks tab,
// which lists nothing else, so the rest show their details here instead.
function TaskLine({ task, currentUserId, onOpen }) {
  const meta = STATUS_META[normalizeStatus(task?.status)] || STATUS_META.unassigned;
  const open = isOpenTask(task);
  const overdue = open && isOverdue(task?.due_date || "");
  const assignee = task?.assigned_to_name || task?.assigned_to_email;
  const assigner = task?.assigned_by_name || task?.assigned_by_email;
  const visit = shortVisitDate(task?.source_visit_date);
  const body = (
    <>
      <div className={`break-words leading-snug ${open ? "text-slate-800" : "text-slate-500"}`}>
        {task?.title || "Untitled task"}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-500">
        <span className={`rounded-full px-1.5 py-px font-medium ring-1 ${meta.tw}`}>{meta.label}</span>
        {assignee && <span>To {assignee}</span>}
        {assigner && assigner !== assignee && <span>From {assigner}</span>}
        {open && task?.due_date && (
          <span className={overdue ? "font-semibold text-rose-600" : undefined}>
            Due {shortVisitDate(task.due_date)}
          </span>
        )}
        {visit && <span>{visit} visit</span>}
      </div>
    </>
  );
  return (
    <li>
      {isOnTask(task, currentUserId) ? (
        <button
          type="button"
          onClick={onOpen}
          title="Open in Tasks"
          className="-mx-1.5 w-[calc(100%+0.75rem)] rounded-md px-1.5 py-1 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {body}
        </button>
      ) : (
        <div className="py-1">{body}</div>
      )}
    </li>
  );
}

function TasksAction({ tasks, status, currentUserId }) {
  const sorted = useMemo(() => sortTasks(tasks), [tasks]);
  const open = sorted.filter(isOpenTask);
  const closed = sorted.filter((task) => !isOpenTask(task));
  const loading = isLoading(status);
  const failed = status === "error";
  const summary = loading
    ? "loading"
    : failed
      ? "couldn't load"
      : open.length
        ? `${open.length} open`
        : "none open";
  const focusTask = (onClose, id) => {
    onClose();
    openMessages({ tab: "tasks", focus: { kind: "task", id } });
  };
  const lines = (list, onClose) =>
    list.map((task) => (
      <TaskLine
        key={task.id}
        task={task}
        currentUserId={currentUserId}
        onOpen={() => focusTask(onClose, task.id)}
      />
    ));
  return (
    <ActionPopover
      label="Related tasks"
      width="20rem"
      trigger={
        <button
          type="button"
          className={`${CHIP} ${WIDTH.tasks} ${open.length ? TONE.indigo : TONE.muted}`}
          aria-label={`Tasks: ${summary}`}
          title={`Tasks: ${summary}`}
        >
          <FiCheckSquare aria-hidden="true" className={ICON} />
          {open.length > 0 && <span>{open.length}</span>}
        </button>
      }
    >
      {({ onClose }) => (
        <div className="text-xs">
          <div className="text-sm font-semibold text-slate-900">Related tasks</div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            Every task made from this patient's charts, from any visit.
          </div>
          {loading && <p className="mt-2 text-slate-500">Loading tasks…</p>}
          {failed && <p className="mt-2 text-rose-600">Couldn't load tasks. Try again in a minute.</p>}
          {!loading && !failed && sorted.length === 0 && (
            <p className="mt-2 text-slate-500">No tasks for this patient.</p>
          )}
          {sorted.length > 0 && (
            <div className={SCROLL_LIST}>
              {open.length > 0 && (
                <>
                  <div className={SECTION}>Open · {open.length}</div>
                  <ul className={`${LIST} space-y-0.5`}>{lines(open, onClose)}</ul>
                </>
              )}
              {closed.length > 0 && (
                <>
                  <div className={SECTION}>Closed · {closed.length}</div>
                  <ul className={`${LIST} space-y-0.5`}>{lines(closed, onClose)}</ul>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className={`${SECONDARY} mt-3`}
            onClick={() => {
              onClose();
              openMessages({ tab: "tasks" });
            }}
          >
            Open my Tasks
          </button>
        </div>
      )}
    </ActionPopover>
  );
}

// ── One row ─────────────────────────────────────────────────────────────────

// `permissions` decides which chips render; a callback that is null hides the
// control that would call it.
export function ScheduleRowActions({
  row,
  data,
  status = {},
  permissions = {},
  // Whose Tasks tab a related task can open in (only tasks they are on).
  currentUserId = null,
  onSetConfirmationStatus = null,
  onOpenOrders = null,
  onOpenMessages = null,
}) {
  return (
    <div className="flex items-center gap-1.5">
      {permissions.confirm && (
        <ConfirmAction
          row={row}
          onSetStatus={onSetConfirmationStatus}
          onViewMessages={permissions.messages ? onOpenMessages : null}
        />
      )}
      {permissions.orders && (
        <OrdersAction
          row={row}
          items={data?.orderItems || []}
          status={status.orders}
          onOpenOrders={onOpenOrders}
        />
      )}
      {permissions.messages && onOpenMessages && (
        <MessagesAction
          row={row}
          unread={data?.unread || 0}
          hasPhone={Boolean(data?.hasPhone)}
          onOpen={onOpenMessages}
        />
      )}
      {permissions.tasks && (
        <TasksAction tasks={data?.tasks || []} status={status.tasks} currentUserId={currentUserId} />
      )}
    </div>
  );
}

// Which chips a role gets. Keys live in roles.json under schedules.components.
export function rowActionPermissions(userRole, { enabled = true, canSetConfirmation = false } = {}) {
  const on = Boolean(enabled && userRole && !isPatientRole(userRole));
  return {
    confirm: on && canAccessComponent(userRole, "schedules.rowConfirm"),
    editConfirm: on && canSetConfirmation && hasClinicalAdminPermissions(userRole),
    orders: on && canAccessComponent(userRole, "schedules.rowOrders"),
    messages: on && canAccessComponent(userRole, "schedules.rowMessages"),
    tasks: on && canAccessComponent(userRole, "schedules.rowTasks"),
  };
}

// ── Host ────────────────────────────────────────────────────────────────────

// Render-prop host: `children(renderRowActions)`, where renderRowActions is
// null when this viewer gets no chips at all.
export default function ScheduleRowActionsHost({
  enabled = true,
  rows,
  userRole,
  homeSchema = null,
  selectedDate = "",
  currentUserId = null,
  currentUserName = "",
  onSetConfirmationStatus = null,
  children,
}) {
  const canSetConfirmation = typeof onSetConfirmationStatus === "function";
  const permissions = useMemo(
    () => rowActionPermissions(userRole, { enabled, canSetConfirmation }),
    [userRole, enabled, canSetConfirmation],
  );
  const anyAction = permissions.confirm || permissions.orders || permissions.messages || permissions.tasks;
  const fallbackSchema = homeSchema || supabaseSchema;

  const { byRow, status, refreshOrders, refreshMessages } = useScheduleRowActionData({
    rows,
    fallbackSchema,
    orders: permissions.orders,
    messages: permissions.messages,
    tasks: permissions.tasks,
  });

  const [ordersRow, setOrdersRow] = useState(null);
  const [messagesTarget, setMessagesTarget] = useState(null);
  const openOrders = useCallback((row) => setOrdersRow(row), []);
  const openPatientMessages = useCallback((row, unread = 0) => setMessagesTarget({ row, unread }), []);

  const renderRowActions = (row) => (
    <ScheduleRowActions
      row={row}
      data={byRow[row?.id]}
      status={status}
      permissions={permissions}
      currentUserId={currentUserId}
      onSetConfirmationStatus={permissions.editConfirm ? onSetConfirmationStatus : null}
      onOpenOrders={permissions.orders ? openOrders : null}
      onOpenMessages={permissions.messages ? openPatientMessages : null}
    />
  );

  return (
    <>
      {children(anyAction ? renderRowActions : null)}
      {ordersRow && (
        <ClinicPatientOrdersModal
          patient={ordersRow}
          schema={rowSchemaOf(ordersRow, fallbackSchema)}
          // Items added here are dated to this appointment; the list itself
          // opens on every date, which is what the row's count covered.
          visitDate={ordersRow.date_ || selectedDate || ""}
          initialScope="all"
          currentUserId={currentUserId}
          signerName={currentUserName || ""}
          currentUserName={currentUserName || ""}
          onClose={() => {
            setOrdersRow(null);
            refreshOrders();
          }}
        />
      )}
      {messagesTarget && (
        <PatientMessagingModal
          isOpen
          onClose={() => {
            setMessagesTarget(null);
            // Opening the thread marks texts reviewed; give that write a
            // moment before recounting (SchedulesTable waits the same).
            setTimeout(refreshMessages, 500);
          }}
          patientData={messagesTarget.row}
          patientId={messagesTarget.row.id}
          unreadCount={messagesTarget.unread}
          appointmentDate={messagesTarget.row.date_ || selectedDate}
        />
      )}
    </>
  );
}
